import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { useMe } from "@/hooks/use-auth";
import {
  useCreateRawMaterial,
  useRawMaterials,
  useUpdateRawMaterial,
} from "@/hooks/use-raw-materials";
import { useUnitConversions } from "@/hooks/use-unit-conversions";
import { useUoms } from "@/hooks/use-uoms";

import {
  defaultRawMaterialValues,
  rawMaterialSchema,
  type RawMaterialFormValues,
} from "./raw-material-schema";

import type { RawMaterialCategory } from "@artisancode/api-types";
import type { RawMaterial } from "@artisancode/api-types";

export function useRawMaterialForm(
  open: boolean,
  editing: RawMaterial | null,
  onSuccess: () => void,
) {
  const { data: uomData } = useUoms();
  const uomList = (uomData?.items ?? []).filter(
    (u) => u.isActive || u.id === editing?.uomId,
  );

  const { data: conversionData } = useUnitConversions({ per_page: 500 });
  const conversions = conversionData?.items ?? [];

  const { data: ingredientData } = useRawMaterials({
    kind: "ingredient",
    per_page: 500,
  });
  const ingredientList = (ingredientData?.items ?? []).filter(
    (i) => i.isActive || i.id === editing?.ingredientId,
  );

  const { mutate: create, isPending: isCreating } = useCreateRawMaterial();
  const { mutate: update, isPending: isUpdating } = useUpdateRawMaterial();
  const { data: me } = useMe();
  const isSaving = isCreating || isUpdating;

  const form = useForm<RawMaterialFormValues>({
    resolver: zodResolver(rawMaterialSchema),
    defaultValues: defaultRawMaterialValues,
  });

  useEffect(() => {
    if (open) {
      form.reset(
        editing
          ? {
              name: editing.name,
              sku: editing.sku ?? "",
              category: editing.category,
              kind: editing.kind,
              uomId: editing.uomId,
              ingredientId: editing.ingredientId ?? "",
              minStock: editing.minStock ?? undefined,
              notes: editing.notes ?? "",
            }
          : { ...defaultRawMaterialValues, workspaceId: me?.workspaceId ?? "" },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, me?.workspaceId]);

  function onSubmit(values: RawMaterialFormValues) {
    const isPurchased = values.kind === "purchased";
    const payload = {
      name: values.name,
      sku: values.sku || null,
      category: values.category as RawMaterialCategory,
      kind: values.kind,
      uomId: values.uomId,
      ingredientId: isPurchased ? values.ingredientId || null : null,
      minStock: values.minStock ?? null,
      notes: values.notes || null,
      workspaceId: values.workspaceId || undefined,
    };
    if (editing) {
      // Note: sku is intentionally immutable on edit - never updated!
      update(
        {
          id: editing.id,
          name: payload.name,
          category: payload.category,
          kind: payload.kind,
          uomId: payload.uomId,
          ingredientId: payload.ingredientId,
          minStock: payload.minStock,
          notes: payload.notes,
        },
        {
          onSuccess: () => {
            toast.success("Bahan baku berhasil diperbarui.");
            onSuccess();
          },
        },
      );
    } else {
      create(payload, {
        onSuccess: () => {
          toast.success("Bahan baku berhasil ditambahkan.");
          onSuccess();
        },
      });
    }
  }

  return {
    form,
    uomList,
    ingredientList,
    conversions,
    isSaving,
    onSubmit: form.handleSubmit(onSubmit),
  };
}
